import React, { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Mail, X } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api.js';
import MergeTagChips from '../marketing/MergeTagChips.jsx';

const field = 'w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/35 focus:border-civix-purple/60 focus:outline-hidden';

/** Why an email can't be sent, or ''. */
export function emailProblem({ to, subject, body }) {
  if (!String(to || '').trim()) return 'This lead has no email address — add one with the edit button.';
  if (!subject.trim()) return 'Add a subject';
  if (!body.trim()) return 'Write your message';
  return '';
}

/**
 * Send an email to the lead from the lead profile: template picker, subject and body.
 * @param {{lead: {email?: string, firstName?: string, emailOptOut?: boolean}, leadId: string, onClose: () => void, onSent?: () => void}} props
 */
export default function EmailComposerModal({ lead, leadId, onClose, onSent }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const ref = useRef(null);
  const { data } = useQuery({ queryKey: ['email-templates'], queryFn: () => apiGet('/marketing/templates').catch(() => null), staleTime: 60_000 });
  const templates = (data?.templates || []).filter((t) => t.body);

  function pick(id) {
    const t = templates.find((x) => String(x._id) === id);
    if (!t) return;
    const m = /^Subject:\s*(.*?)\r?\n+([\s\S]*)$/i.exec(String(t.body));
    setSubject(m ? m[1] : (t.subject || t.name || ''));
    setBody(m ? m[2] : t.body);
  }

  async function send(e) {
    e.preventDefault();
    const problem = emailProblem({ to: lead?.email, subject, body });
    if (problem) { setError(problem); return; }
    if (lead?.emailOptOut) { setError('This lead has unsubscribed from emails.'); return; }
    setSending(true);
    setError('');
    try {
      await apiPost(`/leads/${leadId}/email`, { subject: subject.trim(), body: body.trim() });
      toast.success(`Email sent to ${lead.email}`);
      onSent?.();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.error || 'Could not send the email';
      setError(msg);
      toast.error(msg);
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-label="Send email" data-testid="email-composer" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={send} className="panel w-full max-w-lg space-y-3 p-5">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold"><Mail className="h-4 w-4 text-civix-purpleSoft" /> Send Email</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-white/50 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <label className="block"><span className="mb-1 block text-[11px] text-white/50">To</span>
          <input value={lead?.email || ''} readOnly className={`${field} opacity-70`} data-testid="email-to" aria-label="To" />
        </label>
        <label className="block"><span className="mb-1 block text-[11px] text-white/50">Template (optional)</span>
          <select defaultValue="" onChange={(e) => pick(e.target.value)} className={field} data-testid="email-template">
            <option value="" className="text-black">{templates.length ? 'Choose a template…' : 'No templates yet'}</option>
            {templates.map((t) => <option key={t._id} value={t._id} className="text-black">{t.name}</option>)}
          </select>
        </label>
        <label className="block"><span className="mb-1 block text-[11px] text-white/50">Subject</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} className={field} data-testid="email-subject" />
        </label>
        <label className="block"><span className="mb-1 block text-[11px] text-white/50">Message</span>
          <textarea ref={ref} value={body} onChange={(e) => setBody(e.target.value)} rows={8} className={`${field} font-mono`} data-testid="email-body" />
        </label>
        <MergeTagChips value={body} onChange={setBody} textareaRef={ref} max={20000} testId="email-chips" />
        {error && <p className="text-xs text-rose-300" role="alert" data-testid="email-error">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-ghost text-xs">Cancel</button>
          <button type="submit" disabled={sending} className="btn-primary text-xs disabled:opacity-50" data-testid="email-send">{sending ? 'Sending…' : 'Send Email'}</button>
        </div>
      </form>
    </div>
  );
}
